import Vue from 'vue'

export default class Form extends Vue {
	constructor({ defaults, validator = null, json = false }) {
		super({
			data: {
				fields: Form.wrap(defaults),
				errors: {},
				validating: false
			},
			computed: {
				valid() {
					return !this.validating && Object.keys(this.errors).length === 0
				}
			}
		})

		this.defaults = { ...defaults }
		this.validator = validator

		// Set serialize function based on json flag
		this.serialize = json ? this.toObject : this.toMultiPart

		// Watch form fields for changes
		this.$watch('fields', this.validate.bind(this), { deep: true })
	}

	validate() {
		this.validating = true
		this.validator(this.toObject())
			.then(() => {
				this.errors = {}
				this.validating = false
			})
			.catch(errors => {
				this.errors = errors
				this.validating = false
			})
	}

	populate(data) {
		for (const key of Object.keys(this.defaults)) {
			if (data.hasOwnProperty(key)) this.fields[key].value = data[key]
		}
	}

	reset() {
		for (const [key, rawValue] of Object.entries(this.defaults)) {
			this.fields[key].value = Form.resolveValue(rawValue)
			this.fields[key].$dirty = false
		}
	}

	/**
	 * Get all form fields, optionally injecting reCAPTCHA token.
	 *
	 * @param {Object} [options={}] Extra serialize options.
	 * @param {String} [options.token] Google reCAPTCHAv3 token.
	 * @returns {Array[]} Fields as [key, value] entries.
	 * @memberof Form
	 */
	getFields({ token } = {}) {
		const fields = Form.unwrap(this.fields)
		if (token) fields.token = token
		return Object.entries(fields)
	}

	/**
	 * Serialize the form in its current state, trimming
	 * and nullifying strings.
	 *
	 * @param {Object} [options] Extra serialize options.
	 * @memberof Form
	 */
	toObject(options) {
		return this.getFields(options).reduce(
			(acc, [k, v]) => ({ ...acc, [k]: Form.sanitize(v) }),
			{}
		)
	}

	/**
	 * Converts the form to a FormData object suitable
	 * for sending to Strapi.
	 *
	 * @param {Object} [options] Extra serialize options.
	 * @returns {FormData} The form data.
	 * @memberof Form
	 */
	toMultiPart(options) {
		// Build form payload
		const payload = this.getFields(options)

		// Prepare form data object
		const formData = new FormData()
		const data = {}

		for (const [k, v] of payload) {
			if (v instanceof FileList) {
				// Add to form data files
				const file = Form.sanitize(v)
				formData.append(`files.${k}`, file, file.name)
			} else {
				// Add all other data to a data object
				data[k] = Form.sanitize(v)
			}
		}

		// Append data object to form data
		formData.append('data', JSON.stringify(data))
		return formData
	}

	/**
	 * Sanitizes a form value based on its type.
	 *
	 * @static
	 * @param {*} value The value to sanitize.
	 * @returns {*} The sanitized value.
	 * @memberof Form
	 */
	static sanitize(value) {
		if (typeof value === 'string') {
			const trimmed = value.trim()
			return trimmed === '' ? null : trimmed
		} else if (value instanceof FileList) {
			return value.item(0)
		} else {
			return value
		}
	}

	/**
	 * Wraps a set of fields into form field objects.
	 *
	 * @static
	 * @param {object} fields Raw form fields as key value pairs.
	 * @returns {object} Wrapped fields.
	 * @memberof Form
	 */
	static wrap(fields) {
		return Object.entries(fields).reduce((acc, [key, rawValue]) => {
			const field = { value: Form.resolveValue(rawValue), $dirty: false }
			return { ...acc, [key]: field }
		}, {})
	}

	/**
	 * Unwraps form field objects into raw keys and values.
	 *
	 * @static
	 * @param {object} fields Wrapped form fields.
	 * @returns {object} Raw key value pairs.
	 * @memberof Form
	 */
	static unwrap(fields) {
		return Object.entries(fields).reduce((acc, [k, field]) => {
			return { ...acc, [k]: field.value }
		}, {})
	}

	/**
	 * Resolves a default value to an actual value by running any defaults
	 * defined as functions.
	 *
	 * @static
	 * @param {*} rawValue The raw value to resolve.
	 * @returns {*} The resolved value.
	 * @memberof Form
	 */
	static resolveValue(rawValue) {
		return typeof rawValue === 'function' ? rawValue() : rawValue
	}
}
